<!--
 * @Description: 首页柱状图：线索来源统计、按负责人统计
 * @Author: mdb
 * @Date: 2023-03-14 16:43:45
 * @LastEditTime: 2023-03-16 19:02:30
 * @LastEditors: mdb
-->
<template>
  <div class="home-echarts-wrap" :style="{ width, height }">
    <div class="home-echarts-header">
      <h3 class="home-echarts-header__title">{{ title }}</h3>
      <div class="home-echarts-header__controls">
        <CosRangePicker
          v-model:startTime="queryForm.startTime"
          v-model:endTime="queryForm.endTime"
          :allowClear="props.allowClear"
          @change="getData"
        />
        <CosSelect
          v-model:value="queryForm.depCode"
          :allowClear="props.allowClear"
          style="width: 145px; margin-left: 10px"
          @change="getData"
        >
          <a-select-option value="all">{{ props.isMultiLanguage ? t('home.jio30c') : '全部数据' }}</a-select-option>
          <a-select-option value="my">{{ props.isMultiLanguage ? t('home.dd13al') : '我的数据' }}</a-select-option>
        </CosSelect>
      </div>
    </div>
    <!-- 延迟显示 loading 效果。当 spinning 状态在 delay 时间内结束，则不显示 loading 状态。 -->
    <a-spin :spinning="spinning" :delay="200">
      <!-- echarts 图表 -->
      <div ref="chartRef" class="home-echarts-content" v-show="showECharts"></div>
      <dc-layout-empty type="nothing" v-if="!showECharts" />
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onBeforeMount, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useECharts } from '@/hooks/useECharts'
import { getCurrentMonthDateRange, toBeijingTime } from '@/utils/timeFormat'
import { useInteractionStore } from '@/stores/interaction'
import { sourceList as sourceListFbu, userList as userListFbu } from '@/api/gucangSales/index'
import { sourceList as sourceListOfbu, userList as userListOfbu } from '@/api/overseasSales/index'
import { sourceList as sourceListLbu, userList as userListLbu } from '@/api/yuntuSales/index'
import type { Ref, PropType } from 'vue'
interface QueryFormType {
  depCode: string
  startTime: string
  endTime: string
}
interface ItemCommonType {
  xLabel: string
  source: string
  userName: string
  customerCount: number
  clueCount: number
  chanceCount: number
  followCount: number
}

const { t } = useI18n()
const { language } = storeToRefs(useInteractionStore())
const { firstDayOfMonth, currentDayOfMonth } = getCurrentMonthDateRange()

const props = defineProps({
  title: {
    type: String as PropType<string>
  },
  appId: {
    type: String as PropType<'cos_fbu' | 'cos_ofbu' | 'cos_lbu'>,
    required: true
  },
  type: {
    type: String as PropType<'lead' | 'user'>,
    required: true
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '437px'
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  // 是否需要转换时区
  isTimeZoneConv: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  // 是否需要支持多语言
  isMultiLanguage: {
    type: Boolean as PropType<boolean>,
    default: false
  }
})

const title = computed(() => {
  const i18nTitleEnum = { lead: t('home.k51tch'), user: t('home.hv78t7') }
  const titleEnum = { lead: '线索来源统计', user: '按负责人统计' }
  return props.title || props.isMultiLanguage ? i18nTitleEnum[props.type] : titleEnum[props.type]
})
// 函数枚举
const leadUserEnum = {
  cos_fbu: { lead: sourceListFbu, user: userListFbu },
  cos_ofbu: { lead: sourceListOfbu, user: userListOfbu },
  cos_lbu: { lead: sourceListLbu, user: userListLbu }
}
const spinning = ref(false)
const showECharts = ref(false)
const queryForm = reactive<QueryFormType>({
  depCode: 'all',
  startTime: firstDayOfMonth,
  endTime: currentDayOfMonth
})
const getData = () => {
  const params = Object.assign({}, queryForm)
  if (props.isTimeZoneConv) {
    params.startTime = toBeijingTime(params.startTime)
    params.endTime = toBeijingTime(params.endTime)
  }
  const fn = leadUserEnum[props.appId][props.type]
  fn(params)
    .then((res) => {
      const data = res.data as unknown as ItemCommonType[]
      if (data.length === 0) {
        showECharts.value = false
        initChart([])
      } else {
        showECharts.value = true
        const keyEnum = { lead: 'source', user: 'userName' }
        data.forEach((item) => (item.xLabel = item[keyEnum[props.type]]))
        initChart(data)
      }
    })
    .finally(() => {
      spinning.value = false
    })
}

const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>)
const initChart = (data: ItemCommonType[]) => {
  setOptions({
    tooltip: {
      trigger: 'axis'
    },
    legend: {
      data: [t('home.2elm35'), t('home.q9wxf8'), t('home.4z5hih'), t('home.9onhsr')],
      right: '50px'
    },
    toolbox: {
      feature: {
        magicType: {
          show: false,
          type: ['line', 'bar']
        }
      }
    },
    grid: {
      left: '80',
      right: '50',
      bottom: '50'
    },
    calculable: false,
    xAxis: [
      {
        type: 'category',
        data: data.map((item) => item.xLabel),
        axisLabel: {
          // 倾斜的程度
          formatter: function (value: any) {
            if (value.length > 7) {
              return `${value.slice(0, 7)}...`
            }
            return value
          }
        }
      }
    ],
    yAxis: [
      {
        type: 'value',
        axisLine: {
          show: false,
          lineStyle: {
            type: 'dashed'
          }
        },
        axisLabel: {
          // 倾斜的程度
          margin: 2,
          formatter: function (value: any) {
            // 如果是谷仓海外销售，不需要转换
            if (props.appId === 'cos_ofbu') return value

            if (value >= 10000 && value < 100000000) {
              value = value / 10000 + '万'
            } else if (value >= 100000000 && value < 1000000000000) {
              value = value / 100000000 + '亿'
            } else if (value >= 1000000000000) {
              value = value / 1000000000000 + '兆'
            }
            return value
          }
        },
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        }
      }
    ],
    series: [
      {
        name: t('home.2elm35'),
        type: 'bar',
        itemStyle: {
          color: '#3F97FF'
        },
        data: data.map((item) => item.customerCount)
      },
      {
        name: t('home.q9wxf8'),
        type: 'bar',
        itemStyle: {
          color: '#877CED'
        },
        data: data.map((item) => item.clueCount)
      },
      {
        name: t('home.4z5hih'),
        type: 'bar',
        itemStyle: {
          color: '#36C8E1'
        },
        data: data.map((item) => item.chanceCount)
      },
      {
        name: t('home.9onhsr'),
        type: 'bar',
        itemStyle: {
          color: '#FFB648'
        },
        data: data.map((item) => item.followCount)
      }
    ]
  })
}
onBeforeMount(() => getData())
if (props.isMultiLanguage) {
  watch(
    () => language.value,
    () => getData()
  )
}
</script>
<script lang="ts">
export default { name: 'HomePersonBarChar' }
</script>

<style lang="scss" scoped></style>
